import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../utils/enums';
import {
  GetFinanceArchiveMeta,
  SearchFinanceArchiveTransactions,
  GetFinanceArchiveTransaction,
  SearchFinanceArchiveAccounts,
  SearchFinanceArchiveContacts,
  SearchFinanceArchiveDocuments,
  DownloadFinanceArchiveDocument,
} from '../../services/backend';
import {
  TYPE_OPTIONS,
  emptyFilters,
  formatPkrRs,
  formatArchiveDate,
  entityChip,
} from './utils';
import './finance-archive.css';

const TITLES = {
  search: 'Search archive',
  results: 'Search results',
  drill: 'Txn drill-through',
  docs: 'Archive documents',
  coa: 'Chart of Accounts',
  denied: 'Access denied',
};

const emptyDocFilters = () => ({
  fileName: '',
  contentType: '',
  hasGridFs: 'true',
  dateFrom: '',
  dateTo: '',
  parentEntityType: '',
});

const formatBytes = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
};

const isArchiveAdmin = (userDetails) => {
  if (!userDetails) return false;
  if (userDetails.role === STATIC_USER_ROLES.SUPER_ADMIN) return true;
  if (userDetails.role === STATIC_USER_ROLES.ADMIN) return true;
  if (userDetails.role === STATIC_USER_ROLES.STAFF) {
    const perms = userDetails.dynamicRole?.permissions || [];
    if (perms.includes('FX34') || perms.includes('FX34:view')) return true;
    if (userDetails.dynamicRole?.name === 'Administrator') return true;
  }
  return false;
};

const FinanceArchivePage = () => {
  const { userDetails } = useAuth();
  const admin = isArchiveAdmin(userDetails);

  const [view, setView] = useState(admin ? 'search' : 'denied');
  const [filters, setFilters] = useState(emptyFilters);
  const [coaQuery, setCoaQuery] = useState({ q: '', status: 'all' });
  const [results, setResults] = useState(null);
  const [detail, setDetail] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contactHints, setContactHints] = useState([]);
  const [docFilters, setDocFilters] = useState(emptyDocFilters);
  const [documents, setDocuments] = useState(null);

  useEffect(() => {
    if (!admin) {
      setView('denied');
      return;
    }
    GetFinanceArchiveMeta().catch(() => {});
  }, [admin]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => setFilters(emptyFilters());

  const loadCoa = useCallback(async (override = {}) => {
    if (!admin) return;
    setLoading(true);
    setError(null);
    const q = { ...coaQuery, ...override };
    try {
      const { data } = await SearchFinanceArchiveAccounts({
        q: q.q || undefined,
        status: q.status === 'all' ? undefined : q.status,
        limit: 300,
      });
      setAccounts(data);
      setView('coa');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) setView('denied');
      else setError(err?.response?.data?.message || err.message || 'CoA load failed');
    } finally {
      setLoading(false);
    }
  }, [admin, coaQuery]);

  const setDocFilter = (key, value) => {
    setDocFilters((prev) => ({ ...prev, [key]: value }));
  };

  const loadDocuments = useCallback(async (override = {}) => {
    if (!admin) return;
    setLoading(true);
    setError(null);
    const q = { ...docFilters, ...override };
    try {
      const { data } = await SearchFinanceArchiveDocuments({
        fileName: q.fileName || undefined,
        contentType: q.contentType || undefined,
        hasGridFs: q.hasGridFs === 'all' ? undefined : q.hasGridFs,
        dateFrom: q.dateFrom || undefined,
        dateTo: q.dateTo || undefined,
        parentEntityType: q.parentEntityType || undefined,
        page: 1,
        limit: 50,
      });
      setDocuments(data);
      setView('docs');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) setView('denied');
      else setError(err?.response?.data?.message || err.message || 'Documents load failed');
    } finally {
      setLoading(false);
    }
  }, [admin, docFilters]);

  const runSearch = useCallback(async () => {
    if (!admin) return;
    if (filters.type === 'coa') {
      await loadCoa();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await SearchFinanceArchiveTransactions({
        q: filters.q || undefined,
        type: filters.type,
        contact: filters.contact || undefined,
        documentNumber: filters.documentNumber || undefined,
        amountMin: filters.amountMin || undefined,
        amountMax: filters.amountMax || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        page: 1,
        limit: 50,
      });
      setResults(data);
      setView('results');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setView('denied');
      } else {
        setError(err?.response?.data?.message || err.message || 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  }, [admin, filters, loadCoa]);

  const openTxn = async (sourceId) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await GetFinanceArchiveTransaction(sourceId);
      setDetail(data);
      setView('drill');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to open transaction');
    } finally {
      setLoading(false);
    }
  };

  const onContactBlurSearch = async () => {
    if (!filters.contact || filters.contact.trim().length < 2) {
      setContactHints([]);
      return;
    }
    try {
      const { data } = await SearchFinanceArchiveContacts({ q: filters.contact, limit: 8 });
      setContactHints(data?.items || []);
    } catch {
      setContactHints([]);
    }
  };

  const downloadDoc = async (doc) => {
    if (!doc?.sourceId) return;
    try {
      const res = await DownloadFinanceArchiveDocument(doc.sourceId);
      const blob = new Blob([res.data], { type: doc.contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || `${doc.sourceId}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Download failed');
    }
  };

  const title = TITLES[view] || 'Finance archive';

  if (!admin || view === 'denied') {
    return (
      <div className="fa-page">
        <div className="fa-banner-archive">ARCHIVED — Workbench-New — Pulse DB — read-only</div>
        <div className="fa-main">
          <div className="fa-card fa-deny">
            <div className="fa-chips" style={{ justifyContent: 'center' }}>
              <span className="fa-chip danger">403</span>
            </div>
            <h2>Admins only</h2>
            <p className="fa-meta" style={{ margin: '0 0 12px' }}>
              Archive UI and RO APIs are allowlisted to <strong>all Admin</strong> role users (WBP-188 / WBP-189).
              Non-Admins cannot open <code>/finance/archive</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fa-page">
      <div className="fa-banner-archive">ARCHIVED — Workbench-New — Pulse DB — read-only</div>
      <div className="fa-main">
        <div className="fa-top">
          <div>
            <h1>{title}</h1>
            <div className="sub">
              Route <code>/finance/archive</code> · Viewing as Admin · en-PK amounts · Workbench-New archive
            </div>
          </div>
          <div className="fa-chips">
            <span className="fa-chip archive">Workbench-New archive</span>
            <span className="fa-chip ok">Admin allowlist</span>
            <span className="fa-chip muted">No mutate APIs</span>
          </div>
        </div>

        <div className="fa-iso">
          <strong>Isolation:</strong> Results read only archive collections (<code>zohoArchiveNew_*</code>).
          Banner above is mandatory. This is not live Exec / ops finance.
        </div>

        <div className="fa-tabs">
          <button type="button" className={`fa-tab ${view === 'search' ? 'active' : ''}`} onClick={() => setView('search')}>Search</button>
          <button type="button" className={`fa-tab ${view === 'results' ? 'active' : ''}`} onClick={() => setView('results')} disabled={!results}>Results</button>
          <button type="button" className={`fa-tab ${view === 'docs' ? 'active' : ''}`} onClick={() => loadDocuments()}>Documents</button>
          <button type="button" className={`fa-tab ${view === 'drill' ? 'active' : ''}`} onClick={() => detail && setView('drill')} disabled={!detail}>Txn drill-through</button>
          <button type="button" className={`fa-tab ${view === 'coa' ? 'active' : ''}`} onClick={() => loadCoa()}>Chart of Accounts</button>
        </div>

        {error ? <div className="fa-error">{error}</div> : null}
        {loading ? <div className="fa-loading">Loading…</div> : null}

        {view === 'search' && (
          <>
            <div className="fa-card">
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="fa-q">Free-text search (general)</label>
                <input
                  id="fa-q"
                  value={filters.q}
                  placeholder="Search transactions — amounts, notes, doc #s, descriptions, Zoho ids… (not limited to contact/vendor)"
                  style={{ fontSize: 14, padding: '11px 12px' }}
                  onChange={(e) => setFilter('q', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                />
                <p className="fa-sample" style={{ marginTop: 6 }}>
                  Finds txns beyond customer/vendor filters · AND with structured filters below · notes, line descs, journal account names, doc #s, amounts-as-text, sourceId
                </p>
              </div>
              <div className="fa-filters">
                <div>
                  <label>Type</label>
                  <select value={filters.type} onChange={(e) => setFilter('type', e.target.value)}>
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>Contact</label>
                  <input
                    value={filters.contact}
                    placeholder="Customer or vendor name"
                    onChange={(e) => setFilter('contact', e.target.value)}
                    onBlur={onContactBlurSearch}
                    list="fa-contact-hints"
                  />
                  <datalist id="fa-contact-hints">
                    {contactHints.map((c) => (
                      <option key={c.sourceId} value={c.contactName} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label>Document #</label>
                  <input
                    value={filters.documentNumber}
                    placeholder="INV-… / BILL-…"
                    onChange={(e) => setFilter('documentNumber', e.target.value)}
                  />
                </div>
                <div>
                  <label>Amount min (PKR)</label>
                  <input type="number" value={filters.amountMin} placeholder="0" onChange={(e) => setFilter('amountMin', e.target.value)} />
                </div>
                <div>
                  <label>Amount max (PKR)</label>
                  <input type="number" value={filters.amountMax} placeholder="Any" onChange={(e) => setFilter('amountMax', e.target.value)} />
                </div>
                <div>
                  <label>Date range</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="date" value={filters.dateFrom} onChange={(e) => setFilter('dateFrom', e.target.value)} />
                    <input type="date" value={filters.dateTo} onChange={(e) => setFilter('dateTo', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="fa-row-actions">
                <button type="button" className="fa-btn" onClick={runSearch}>Search archive</button>
                <button type="button" className="fa-btn ghost" onClick={clearFilters}>Clear</button>
                <button type="button" className="fa-btn ghost" disabled title="Audited export comes later">Export CSV (audited)</button>
              </div>
              <p className="fa-sample">Free-text Q + structured filters · zohoArchiveNew_* only · org 801209931</p>
            </div>

            <div className="fa-section-head">Quick CoA lookup</div>
            <div className="fa-card">
              <div className="fa-filters">
                <div>
                  <label>Account code / name</label>
                  <input
                    value={coaQuery.q}
                    placeholder="e.g. 4000 Rent income"
                    onChange={(e) => setCoaQuery((p) => ({ ...p, q: e.target.value }))}
                  />
                </div>
                <div>
                  <label>Status</label>
                  <select
                    value={coaQuery.status}
                    onChange={(e) => setCoaQuery((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="all">All (active + inactive)</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive / historical</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="button" className="fa-btn ghost" onClick={() => loadCoa()}>Open CoA</button>
                </div>
              </div>
            </div>

            <div className="fa-section-head">Archive documents</div>
            <div className="fa-card">
              <p className="fa-meta" style={{ margin: '0 0 10px' }}>
                Browse inbox binaries stored in GridFS even when they are not linked to a transaction yet (per-txn linking is a later step).
              </p>
              <div className="fa-row-actions">
                <button type="button" className="fa-btn" onClick={() => loadDocuments()}>Browse archive documents</button>
              </div>
            </div>
          </>
        )}

        {view === 'results' && (
          <>
            <div className="fa-chips" style={{ marginBottom: 10 }}>
              <span className="fa-chip">{results?.total ?? 0} hits</span>
              <span className="fa-chip muted">Source: Workbench-New archive</span>
            </div>
            <div className="fa-card">
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Type</th><th>Doc #</th><th>Contact</th><th>CoA</th><th>Amount (PKR)</th><th />
                  </tr>
                </thead>
                <tbody>
                  {(results?.items || []).map((row) => {
                    const chip = entityChip(row.entityType);
                    return (
                      <tr key={row.sourceId}>
                        <td>{formatArchiveDate(row.transactionDate)}</td>
                        <td><span className={`fa-chip ${chip.tone}`}>{chip.label}</span></td>
                        <td>{row.documentNumber || row.sourceId}</td>
                        <td>{row.contactName || '—'}</td>
                        <td>{row.coaSummary || '—'}</td>
                        <td className="fa-amt">{formatPkrRs(row.total)}</td>
                        <td>
                          <button type="button" className="fa-btn ghost" onClick={() => openTxn(row.sourceId)}>Open</button>
                        </td>
                      </tr>
                    );
                  })}
                  {!results?.items?.length && !loading ? (
                    <tr><td colSpan={7} className="fa-meta">No archive hits for these filters.</td></tr>
                  ) : null}
                </tbody>
              </table>
              <p className="fa-sample">Amounts shown in en-PK style (Rs). Archive only — not live Pulse invoices.</p>
            </div>
          </>
        )}

        {view === 'drill' && detail && (
          <div className="fa-grid2">
            <div className="fa-card">
              <div className="fa-chips">
                <span className="fa-chip archive">Archived txn</span>
                <span className="fa-chip">{entityChip(detail.entityType).label}</span>
                <span className="fa-chip muted">sourceOrg: workbench-new</span>
              </div>
              <h2 style={{ margin: '10px 0 4px', fontSize: 16 }}>{detail.documentNumber || detail.sourceId}</h2>
              <p className="fa-meta">
                Txn date {formatArchiveDate(detail.transactionDate)} · Zoho sourceId {detail.sourceId} · immutable
              </p>
              <p style={{ margin: '12px 0 0', fontSize: 13 }}>
                <strong>Contact:</strong>{' '}
                {detail.contactName || detail.contact?.contactName || '—'}
                {' '}(archive contact — not linked to live Pulse customer)
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                <strong>Status:</strong> {detail.status || '—'} · <strong>Total:</strong>{' '}
                <span className="fa-amt">{formatPkrRs(detail.total)}</span>
              </p>
              <div className="fa-section-head">Line items</div>
              <table className="fa-table">
                <thead><tr><th>CoA</th><th>Desc</th><th>Amount</th></tr></thead>
                <tbody>
                  {(detail.lineItems || []).length ? detail.lineItems.map((li, idx) => (
                    <tr key={idx}>
                      <td>{[li.accountCode, li.accountName].filter(Boolean).join(' ') || '—'}</td>
                      <td>{li.description || '—'}</td>
                      <td className="fa-amt">{formatPkrRs(li.amount)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={3} className="fa-meta">No line items in archived payload (list-shape ingest).</td></tr>
                  )}
                </tbody>
              </table>
              <div className="fa-row-actions">
                <button type="button" className="fa-btn ghost" disabled title="Read-only">Edit (disabled)</button>
                <button type="button" className="fa-btn ghost" onClick={() => setView('results')}>Back to results</button>
              </div>
            </div>
            <div className="fa-card">
              <div className="fa-section-head" style={{ marginTop: 0 }}>Documents / binaries</div>
              {(detail.documents || []).length ? (
                detail.documents.map((doc) => (
                  <div className="fa-pdf" key={doc.sourceId} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 28 }}>📄</div>
                    <div>{doc.fileName || doc.sourceId}</div>
                    <div className="fa-meta">
                      {doc.hasBinary
                        ? 'Stored in Pulse archive (GridFS) · not metadata-only'
                        : 'Metadata only — binary not yet in GridFS'}
                    </div>
                    <div className="fa-row-actions" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="fa-btn"
                        disabled={!doc.hasBinary}
                        onClick={() => downloadDoc(doc)}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="fa-pdf">
                  <div style={{ fontSize: 28 }}>📄</div>
                  <div>No attachments linked to this transaction</div>
                  <div className="fa-meta">
                    Inbox binaries may still exist in the archive (not linked per-txn yet).
                    Missing files also appear on the export manifest gaps list.
                  </div>
                  <div className="fa-row-actions" style={{ marginTop: 8 }}>
                    <button type="button" className="fa-btn" onClick={() => loadDocuments()}>
                      Browse archive documents
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {view === 'docs' && (
          <>
            <div className="fa-card" style={{ marginBottom: 12 }}>
              <div className="fa-filters">
                <div>
                  <label>Filename</label>
                  <input
                    value={docFilters.fileName}
                    placeholder="e.g. INV-… / receipt"
                    onChange={(e) => setDocFilter('fileName', e.target.value)}
                  />
                </div>
                <div>
                  <label>Content type</label>
                  <input
                    value={docFilters.contentType}
                    placeholder="pdf / image"
                    onChange={(e) => setDocFilter('contentType', e.target.value)}
                  />
                </div>
                <div>
                  <label>Has GridFS binary</label>
                  <select
                    value={docFilters.hasGridFs}
                    onChange={(e) => setDocFilter('hasGridFs', e.target.value)}
                  >
                    <option value="true">Yes (downloadable)</option>
                    <option value="false">No (metadata only)</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div>
                  <label>Parent type</label>
                  <select
                    value={docFilters.parentEntityType}
                    onChange={(e) => setDocFilter('parentEntityType', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="documents_inbox">Inbox (documents_inbox)</option>
                    <option value="invoice">Invoice</option>
                    <option value="bill">Bill</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label>Exported date range</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="date" value={docFilters.dateFrom} onChange={(e) => setDocFilter('dateFrom', e.target.value)} />
                    <input type="date" value={docFilters.dateTo} onChange={(e) => setDocFilter('dateTo', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="fa-row-actions">
                <button type="button" className="fa-btn" onClick={() => loadDocuments()}>Search documents</button>
                <button type="button" className="fa-btn ghost" onClick={() => setDocFilters(emptyDocFilters())}>Clear</button>
                <button type="button" className="fa-btn ghost" onClick={() => setView('search')}>Back to search</button>
              </div>
              <p className="fa-sample">Lists zohoArchiveNew_documents · download via GridFS when gridFsId present</p>
            </div>
            <div className="fa-chips" style={{ marginBottom: 10 }}>
              <span className="fa-chip">{documents?.total ?? 0} docs</span>
              <span className="fa-chip archive">Inbox binaries OK</span>
              <span className="fa-chip muted">Per-txn link = later (A3)</span>
            </div>
            <div className="fa-card">
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Exported</th><th>Filename</th><th>Type</th><th>Size</th><th>Parent</th><th>Binary</th><th />
                  </tr>
                </thead>
                <tbody>
                  {(documents?.items || []).map((doc) => (
                    <tr key={doc.sourceId}>
                      <td>{formatArchiveDate(doc.exportedAt)}</td>
                      <td>{doc.fileName || doc.sourceId}</td>
                      <td>{doc.contentType || '—'}</td>
                      <td>{formatBytes(doc.size)}</td>
                      <td>
                        {doc.parentEntityType || '—'}
                        {doc.parentSourceId ? ` · ${doc.parentSourceId}` : ''}
                      </td>
                      <td>
                        <span className={`fa-chip ${doc.hasBinary ? 'ok' : 'muted'}`}>
                          {doc.hasBinary ? 'GridFS' : 'None'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="fa-btn ghost"
                          disabled={!doc.hasBinary}
                          onClick={() => downloadDoc(doc)}
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!documents?.items?.length && !loading ? (
                    <tr><td colSpan={7} className="fa-meta">No archive documents for these filters.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === 'coa' && (
          <>
            <div className="fa-chips" style={{ marginBottom: 10 }}>
              <span className="fa-chip">Full CoA</span>
              <span className="fa-chip muted">Active + inactive</span>
              <span className="fa-chip archive">Archive only</span>
              <span className="fa-chip muted">{accounts?.total ?? 0} accounts</span>
            </div>
            <div className="fa-card">
              <table className="fa-table">
                <thead>
                  <tr><th>Code</th><th>Name</th><th>Type</th><th>Status</th><th>Parent</th></tr>
                </thead>
                <tbody>
                  {(accounts?.items || []).map((a) => (
                    <tr key={a.sourceId}>
                      <td>{a.accountCode || '—'}</td>
                      <td>{a.accountName || '—'}</td>
                      <td>{a.accountType || '—'}</td>
                      <td>
                        <span className={`fa-chip ${a.isActive ? 'ok' : 'muted'}`}>
                          {a.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{a.parentAccountId || '—'}</td>
                    </tr>
                  ))}
                  {!accounts?.items?.length && !loading ? (
                    <tr><td colSpan={5} className="fa-meta">No CoA rows in archive yet.</td></tr>
                  ) : null}
                </tbody>
              </table>
              <p className="fa-sample">Historical/inactive included per PRD-003</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceArchivePage;
